<section class="sipwatch-page">
  <header class="sipwatch-page__heading">
    <div>
      <span class="eyebrow">SIP phone monitoring</span>
      <h4>SIPWatch Watch List</h4>
      <p>Manage watched phones and review their latest SIP registration status.</p>
    </div>
    <button mat-stroked-button type="button" color="primary" (click)="loadState(true)" [disabled]="loading || mutating">
      <mat-spinner *ngIf="loading" diameter="16"></mat-spinner>
      <mat-icon *ngIf="!loading">refresh</mat-icon>{{ loading ? 'Refreshing...' : 'Refresh status' }}
    </button>
  </header>

  <div class="sipwatch-error" *ngIf="error" role="alert">
    <mat-icon>error_outline</mat-icon><span>{{ error }}</span>
    <button mat-button type="button" (click)="loadState(true)">Retry</button>
  </div>

  <section class="sipwatch-section watched-phones">
    <header class="section-heading">
      <div><h5>Watched phones</h5><p>Phones in this list can raise SIP registration alerts.</p></div>
      <button mat-raised-button type="button" class="primary-action" (click)="openAddPhone()" [disabled]="mutating">
        <mat-icon>add_call</mat-icon>Add new phone
      </button>
    </header>

    <div class="empty-state" *ngIf="!loading && !watchList.length">
      <mat-icon>phonelink_off</mat-icon>
      <strong>No watched phones yet</strong>
      <span>Add a phone manually or use the add button beside a detected registration below.</span>
    </div>

    <div class="table-wrap" *ngIf="watchList.length">
      <table class="watch-table">
        <thead><tr><th>SIP ID / extension</th><th>Phone name</th><th>Server port</th><th>Alerts</th><th>Alarm</th><th class="actions-column">Actions</th></tr></thead>
        <tbody>
          <tr *ngFor="let phone of watchList; trackBy: trackByPhone">
            <td class="mono">{{ phone.sip_user_id || '—' }}</td>
            <td><strong>{{ phone.sip_name || 'Unnamed phone' }}</strong></td>
            <td class="mono">{{ phone.server_port || '—' }}</td>
            <td><span class="state-pill" [class.state-pill--on]="phone.alert">{{ phone.alert ? 'On' : 'Off' }}</span></td>
            <td><span class="state-pill" [class.state-pill--alarm]="phone.alarm === 'alarm'" [class.state-pill--ok]="phone.alarm === 'ok'">{{ alarmLabel(phone.alarm) }}</span></td>
            <td class="row-actions">
              <button mat-icon-button type="button" class="edit-icon" matTooltip="Edit watched phone" aria-label="Edit watched phone" (click)="openEditPhone(phone)" [disabled]="mutating"><mat-icon>edit</mat-icon></button>
              <button mat-icon-button type="button" class="danger-icon" matTooltip="Remove watched phone" aria-label="Remove watched phone" (click)="requestDeletePhone(phone)" [disabled]="mutating"><mat-icon>delete_outline</mat-icon></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>

  <section class="sipwatch-section status-section">
    <header class="section-heading status-heading">
      <div><h5>Registration status</h5><p>The latest detected SIP registration result for each phone.</p></div>
      <div class="bulk-actions" *ngIf="tracks.length">
        <button mat-stroked-button type="button" color="warn" (click)="requestBulkAction('clear_all')" [disabled]="mutating"><mat-icon>delete_sweep</mat-icon>Clear all</button>
        <button mat-raised-button type="button" class="success-action" (click)="requestBulkAction('reregister_all')" [disabled]="mutating"><mat-icon>restart_alt</mat-icon>Clear &amp; re-register all</button>
      </div>
    </header>

    <div class="status-summary">
      <div><span>Detected phones</span><strong>{{ summary.total }}</strong></div>
      <div class="status-summary--ok"><span><mat-icon>check_circle</mat-icon>200 OK</span><strong>{{ summary.ok }}</strong></div>
      <div class="status-summary--bad"><span><mat-icon>error</mat-icon>401 Unauthorized</span><strong>{{ summary.unauthorized }}</strong></div>
    </div>

    <p class="status-legend"><strong>Flow:</strong> (A) Assured, (U) Unreplied <span></span><strong>Path:</strong> Ulay is underlay; link3, link4 and link5 are overlay paths.</p>

    <div class="empty-state" *ngIf="!loading && !tracks.length">
      <mat-icon>phone_paused</mat-icon><strong>No SIP registration status reported</strong><span>Refresh after the device detects a SIP registration attempt.</span>
    </div>

    <div class="table-wrap" *ngIf="tracks.length">
      <table class="status-table">
        <thead><tr><th>Event date / time</th><th>SIP ID / ext</th><th>SIP name</th><th>Method</th><th>Status</th><th>Message</th><th>Device IP</th><th>Server IP</th><th>Port</th><th>Watched</th><th>Alert</th><th>Flow</th><th>Path</th><th>Alarm</th><th class="actions-column">Actions</th></tr></thead>
        <tbody>
          <tr *ngFor="let track of tracks; trackBy: trackByStatus">
            <td class="date-cell">{{ trackDate(track.timestamp) | date:'medium' }}</td>
            <td class="mono">{{ track.sip_user_id || '—' }}</td><td>{{ track.sip_name || '—' }}</td><td>{{ track.method || '—' }}</td>
            <td><span class="status-code status-code--{{ statusClass(track) }}">{{ track.status || '—' }}</span></td>
            <td>{{ track.message_state || '—' }}</td><td class="mono">{{ track.device_ip || '—' }}</td><td class="mono">{{ track.server_ip || '—' }}</td><td class="mono">{{ track.server_port || '—' }}</td>
            <td>{{ track.watched ? 'Yes' : 'Detect' }}</td><td>{{ track.alert ? 'On' : 'Off' }}</td><td>{{ track.flow || '—' }}</td><td>{{ track.path || '—' }}</td><td>{{ alarmLabel(track.alarm) }}</td>
            <td class="row-actions status-actions">
              <button *ngIf="!track.watched" mat-icon-button type="button" matTooltip="Add this phone to the Watch List" aria-label="Add phone to Watch List" (click)="addDetectedPhone(track)" [disabled]="mutating"><mat-icon>add_call</mat-icon></button>
              <button mat-icon-button type="button" class="success-icon" matTooltip="Request SIP re-registration" aria-label="Request SIP re-registration" (click)="requestTrackAction('reregister_track', track)" [disabled]="mutating || !track.track"><mat-icon>restart_alt</mat-icon></button>
              <button mat-icon-button type="button" class="danger-icon" matTooltip="Clear this status entry" aria-label="Clear status entry" (click)="requestTrackAction('clear_track', track)" [disabled]="mutating || !track.track"><mat-icon>delete_outline</mat-icon></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</section>

<ng-template #phoneDialog>
  <div class="sipwatch-dialog">
    <header mat-dialog-title><mat-icon>add_call</mat-icon><div><h3>{{ phoneDraft.ptrack ? 'Edit watched phone' : 'Add watched phone' }}</h3><p>{{ deviceName }} · {{ deviceMac | uppercase }}</p></div></header>
    <div mat-dialog-content>
      <p class="dialog-guidance">Enter the SIP account details exactly as they appear on the phone or PBX.</p>
      <div class="phone-form">
        <mat-form-field appearance="outline"><mat-label>SIP ID / extension</mat-label><input matInput [(ngModel)]="phoneDraft.sip_user_id" maxlength="80" required placeholder="4901p"><mat-hint>The phone's SIP user ID or extension</mat-hint></mat-form-field>
        <mat-form-field appearance="outline"><mat-label>Phone name</mat-label><input matInput [(ngModel)]="phoneDraft.sip_name" maxlength="80" required placeholder="Front desk phone"><mat-hint>A clear label support staff will recognize</mat-hint></mat-form-field>
        <mat-form-field appearance="outline"><mat-label>SIP server port</mat-label><input matInput type="number" min="1" max="65535" [(ngModel)]="phoneDraft.server_port" required><mat-hint>Usually 5060</mat-hint></mat-form-field>
        <div class="alert-setting"><mat-icon>notifications_active</mat-icon><div><strong>Registration alerts</strong><span>Notify support when this phone reports a watched SIP condition.</span></div><mat-slide-toggle [(ngModel)]="phoneDraft.alert" aria-label="Enable registration alerts"></mat-slide-toggle></div>
      </div>
    </div>
    <div mat-dialog-actions align="end"><button mat-button type="button" [mat-dialog-close]="false" [disabled]="mutating">Cancel</button><button mat-raised-button type="button" class="primary-action" (click)="savePhone()" [disabled]="mutating"><mat-spinner *ngIf="mutating" diameter="16"></mat-spinner><mat-icon *ngIf="!mutating">save</mat-icon>{{ mutating ? 'Saving...' : 'Save phone' }}</button></div>
  </div>
</ng-template>

<ng-template #confirmDialog>
  <div class="sipwatch-dialog confirm-dialog">
    <header mat-dialog-title><mat-icon>help_outline</mat-icon><div><h3>{{ confirmTitle }}</h3><p>{{ deviceName }} · {{ deviceMac | uppercase }}</p></div></header>
    <div mat-dialog-content><p>{{ confirmMessage }}</p></div>
    <div mat-dialog-actions align="end"><button mat-button type="button" [mat-dialog-close]="false" [disabled]="mutating">Cancel</button><button mat-raised-button type="button" class="primary-action" [class.danger-action]="confirmDanger" (click)="confirmAction()" [disabled]="mutating"><mat-icon>{{ confirmButtonIcon }}</mat-icon>{{ confirmButtonLabel }}</button></div>
  </div>
</ng-template>
